import { parseArtefactSessionPath } from '../utils/artefactSessionPath';

const CONFIGURED_API_BASE = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
const FALLBACK_ORIGIN = 'http://localhost';
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);
const PERSONAS_CACHE_TTL_MS = 30 * 1000;
const personasResponseCache = new Map();
const personasInflightRequests = new Map();

function normalizeConfiguredApiBase(raw) {
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('/')) return raw;
  return `http://${raw}`;
}

/**
 * Resolve backend origin for API calls.
 * - nginx / deployment (e.g. :8181): same-origin ``/api`` when unset or loopback mismatch.
 * - standalone frontend container (:3000): backend on ``:8000`` (no /api proxy on static serve).
 */
export function getApiBaseUrl() {
  const configured = normalizeConfiguredApiBase(CONFIGURED_API_BASE);
  if (typeof window === 'undefined' || !window.location) {
    return configured;
  }

  const { hostname, port, protocol } = window.location;
  const pagePort = port || (protocol === 'https:' ? '443' : '80');

  if (!configured) {
    if (pagePort === '3000') {
      return `${protocol}//${hostname}:8000`;
    }
    return '';
  }

  try {
    const api = new URL(configured);
    const apiLoopback = LOOPBACK_HOSTS.has(api.hostname);
    const pageLoopback = LOOPBACK_HOSTS.has(hostname);
    if (apiLoopback && !pageLoopback) {
      return '';
    }
    if (apiLoopback && pagePort === '3000') {
      return configured;
    }
    if (apiLoopback && pagePort !== '8000' && String(api.port || '80') !== pagePort) {
      return '';
    }
  } catch (_) {
    return configured;
  }

  return configured;
}

function buildApiUrl(path, query = {}) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  let base = getApiBaseUrl();
  if (base && !/^https?:\/\//i.test(base) && !base.startsWith('/')) {
    base = `http://${base}`;
  }

  const origin = typeof window !== 'undefined' && window.location
    ? window.location.origin
    : FALLBACK_ORIGIN;
  const url = new URL(base ? `${base}${normalizedPath}` : normalizedPath, origin);

  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
}

function artefactSegmentFromWindow() {
  if (typeof window === 'undefined' || !window.location) return null;
  const key = parseArtefactSessionPath(window.location.pathname || '')?.key;
  if (key == null || String(key).trim() === '') return null;
  return String(key).trim();
}

class ApiService {
  /**
   * @param {{ persona?: string | null, artefact_segment?: string | null, artefacts_key?: string | null, language?: string | null }} [meta]
   */
  async createSession(meta = null) {
    try {
      const payload =
        meta && typeof meta === 'object'
          ? {
              persona: meta.persona ?? undefined,
              artefact_segment: meta.artefact_segment ?? meta.artefacts_key ?? undefined,
              language: meta.language ?? undefined,
            }
          : {};
      const response = await fetch(`${getApiBaseUrl()}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json().catch(() => ({}));
      const rawId = data?.session_id ?? data?.sessionId ?? null;
      const session_id =
        rawId != null && String(rawId).trim() !== '' ? String(rawId).trim() : null;
      const ok =
        data?.success !== false &&
        session_id !== null;
      return {
        success: ok,
        session_id,
        error: ok ? undefined : data?.error || 'Invalid session response from server',
      };
    } catch (error) {
      console.error('Error creating session:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          session_id: null,
          error: 'Cannot connect to backend server. Please make sure the backend is running.',
        };
      }

      return { success: false, session_id: null, error: error.message };
    }
  }

  /**
   * End a chat on the server: remove LangGraph checkpoint for this session_id (no resume).
   */
  async endSession(sessionId) {
    if (!sessionId) {
      return { success: true, deleted: [] };
    }
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/sessions/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}`,
        };
      }
      return data;
    } catch (error) {
      console.error('Error ending session:', error);
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.',
        };
      }
      return { success: false, error: error.message };
    }
  }

  /**
   * Fetch stored messages for a session (same order as the server DB).
   * Omit limit to return the full transcript.
   */
  async getSessionMessages(sessionId, limit = null) {
    try {
      const query = { session_id: sessionId };
      if (limit != null && limit > 0) {
        query.limit = limit;
      }
      const url = buildApiUrl('/api/messages', query);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching session messages:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.',
        };
      }

      return {
        success: false,
        error: error.message,
      };
    }
  }

  /** LangGraph checkpoint position — used to resume missing awakening beats after reload. */
  async getSessionFlowState(sessionId) {
    try {
      const url = buildApiUrl('/api/sessions/flow-state', { session_id: sessionId });
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching session flow state:', error);
      return { success: false, error: error.message };
    }
  }

  async classifyPrompt(prompt, apiKey = null, sessionId = null) {
    try {
      
      const response = await fetch(`${getApiBaseUrl()}/api/openai/classify-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          api_key: apiKey,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Classification response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling classification API:', error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  async generateText(prompt, model = null, apiKey = null, vectorstoreType = 'documents', systemPrompt = null, sessionId = null, persona = null, language = 'en', isAutoFollowUp = false, inquirySilencePing = false, resumeAfterLongSilenceMinimal = false, pathChoiceId = null, awakeningOptionId = null, memoryHearMoreContinue = false, memoryHearMorePromptText = '', memoryAnotherMemoryAccept = false, deferredMemoryVisitorAck = false, lowEnergyAck = false, skipUserMessagePersist = false) {
    try {
      
      const body = {
        prompt,
        model,
        api_key: apiKey,  // Send API key if provided
        vectorstore_type: vectorstoreType,  // Send vectorstore type
        system_prompt: systemPrompt,  // Send custom system prompt if provided
        session_id: sessionId,  // Send session ID if provided
        persona: persona,  // Send persona if provided
        language: language  // Send language code ('en' or 'el')
      };
      if (isAutoFollowUp) {
        body.is_auto_follow_up = true;  // Do not store messages or update stage (user did not reply)
      }
      if (inquirySilencePing) {
        body.inquiry_silence = true;
      }
      if (resumeAfterLongSilenceMinimal) {
        body.resume_after_long_silence_minimal = true;
      }
      if (pathChoiceId) {
        body.path_choice_id = pathChoiceId;
      }
      if (awakeningOptionId) {
        body.awakening_option_id = awakeningOptionId;
      }
      if (memoryHearMoreContinue) {
        body.memory_hear_more_continue = true;
        if (memoryHearMorePromptText) {
          body.memory_hear_more_prompt = memoryHearMorePromptText;
        }
      }
      if (memoryAnotherMemoryAccept) {
        body.memory_another_memory_accept = true;
      }
      if (deferredMemoryVisitorAck) {
        body.deferred_memory_visitor_ack = true;
      }
      if (lowEnergyAck) {
        body.low_energy_ack = true;
      }
      if (skipUserMessagePersist) {
        body.skip_user_message_persist = true;
      }
      const seg = artefactSegmentFromWindow();
      if (seg) {
        body.artefact_segment = seg;
      }
      const response = await fetch(`${getApiBaseUrl()}/api/openai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling generate API:', error);
      
      // Provide more specific error messages
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  async getHealth() {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/health`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling health API:', error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return { 
          status: 'unhealthy', 
          error: 'Cannot connect to backend server. Please make sure the backend is running.' 
        };
      }
      
      return { 
        status: 'unhealthy', 
        error: error.message 
      };
    }
  }

  async getPersonas(language = 'en') {
    const cacheKey = String(language || 'en').trim() || 'en';
    const now = Date.now();
    const cached = personasResponseCache.get(cacheKey);
    if (cached && now - cached.timestamp < PERSONAS_CACHE_TTL_MS) {
      return cached.data;
    }

    if (personasInflightRequests.has(cacheKey)) {
      return personasInflightRequests.get(cacheKey);
    }

    const request = (async () => {
    try {
      const personasUrl = buildApiUrl('/api/personas', { language: cacheKey });
      const response = await fetch(personasUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      personasResponseCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
      return data;
    } catch (error) {
      console.error('Error calling personas API:', error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }
      
      return {
        success: false,
        error: error.message
      };
    } finally {
      personasInflightRequests.delete(cacheKey);
    }
    })();

    personasInflightRequests.set(cacheKey, request);
    return request;
  }

  async getEthicalCategoryCounts(sessionId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/messages/ethical-categories?session_id=${encodeURIComponent(sessionId)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling ethical category counts API:', error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  } 

  async getInteractionHistory(sessionId, limit = 5) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/messages/interaction-history?session_id=${encodeURIComponent(sessionId)}&limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling interaction history API:', error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Conversation Analytics API Methods

  async getKeywordAnalytics(timeWindow = 24, language = null, since = null) {
    try {
      const lang =
        language != null && String(language).trim() !== '' ? String(language).trim() : null;
      const sinceParam =
        since != null && String(since).trim() !== '' ? String(since).trim() : null;
      const url = buildApiUrl('/api/analytics/keywords', {
        time_window: timeWindow,
        language: lang,
        since: sinceParam,
      });
      const headers = {};
      if (lang) {
        headers['X-UI-Language'] = lang;
      }
      const response = await fetch(url, { cache: 'no-store', headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling keyword analytics API:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }

      return {
        success: false,
        error: error.message
      };
    }
  }

  async triggerKeywordExtraction(sessionId, apiKey = null, language = null) {
    try {
      const body = {
        session_id: sessionId,
        api_key: apiKey,
      };
      if (language != null && String(language).trim() !== '') {
        body.language = String(language).trim();
      }
      const response = await fetch(`${getApiBaseUrl()}/api/analytics/keywords/extract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error triggering keyword extraction:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }

      return {
        success: false,
        error: error.message
      };
    }
  }

  async getSessionKeywords(sessionId, language = null) {
    try {
      const langQ =
        language != null && String(language).trim() !== ''
          ? `?language=${encodeURIComponent(String(language).trim())}`
          : '';
      const path = `${getApiBaseUrl()}/api/analytics/keywords/session/${encodeURIComponent(sessionId)}${langQ}`;
      const headers = {};
      if (language != null && String(language).trim() !== '') {
        headers['X-UI-Language'] = String(language).trim();
      }
      const response = await fetch(path, { cache: 'no-store', headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling session keywords API:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }

      return {
        success: false,
        error: error.message
      };
    }
  }

  // System Prompt Management API Methods

  async getSystemPrompts() {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/system-prompts`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching system prompts:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }

      return {
        success: false,
        error: error.message
      };
    }
  }

  async getActiveSystemPrompt() {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/system-prompts/active`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching active system prompt:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }

      return {
        success: false,
        error: error.message
      };
    }
  }

  async createSystemPrompt(name, promptText, description = null, createdBy = null) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/system-prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          prompt_text: promptText,
          description,
          created_by: createdBy
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating system prompt:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async updateSystemPrompt(promptId, name = null, promptText = null, description = null, createdBy = null) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/system-prompts/${promptId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          prompt_text: promptText,
          description,
          created_by: createdBy
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating system prompt:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async deleteSystemPrompt(promptId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/system-prompts/${promptId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting system prompt:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async activateSystemPrompt(promptId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/system-prompts/${promptId}/activate`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error activating system prompt:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async storeReflection(sessionId, excitement, hope) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/reflections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          excitement: excitement,
          hope: hope
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error storing reflection:', error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to backend server. Please make sure the backend is running.'
        };
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  async getTtsStatus() {
    try {
      const response = await fetch(buildApiUrl('/api/tts/status'), {
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
      });
      if (!response.ok) {
        console.warn('[TTS]', 'GET /api/tts/status failed', { status: response.status });
        return { success: false, enabled: false };
      }
      const data = await response.json();
      return { success: true, enabled: Boolean(data.enabled) };
    } catch (error) {
      console.warn('[TTS]', 'GET /api/tts/status network or parse error', error?.message || error);
      return { success: false, enabled: false };
    }
  }

  /**
   * POST /api/tts — fetch complete MP3 blob.
   * @param {string} text
   * @param {string|null} [voiceId]
   * @param {string|null} [language]
   * @param {AbortSignal} [signal]
   * @returns {Promise<Blob>}
   */
  async textToSpeech(text, voiceId = null, language = null, signal = undefined) {
    const body = { text };
    if (voiceId) {
      body.voice_id = voiceId;
    }
    if (language === 'en' || language === 'el') {
      body.language = language;
    }
    let response;
    try {
      response = await fetch(buildApiUrl('/api/tts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal,
      });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      console.warn('[TTS]', 'POST /api/tts fetch failed', error?.message || error);
      throw error;
    }

    const contentType = response.headers.get('Content-Type') || '';
    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      if (contentType.includes('application/json')) {
        try {
          const errJson = await response.json();
          if (errJson.error) {
            message = errJson.error;
          }
        } catch {
          /* ignore */
        }
      }
      console.warn('[TTS]', 'POST /api/tts failed', { status: response.status, message });
      throw new Error(message);
    }
    if (!contentType.includes('audio')) {
      const textBody = await response.text();
      console.warn('[TTS]', 'Unexpected response type (expected audio)', {
        contentType,
        bodyPreview: textBody.slice(0, 200),
      });
      throw new Error(textBody.slice(0, 200) || 'Unexpected TTS response');
    }
    return response.blob();
  }

  async getSttStatus() {
    try {
      const response = await fetch(buildApiUrl('/api/stt/status'), {
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
      });
      if (!response.ok) {
        console.warn('[STT]', 'GET /api/stt/status failed', { status: response.status });
        return { success: false, enabled: false };
      }
      const data = await response.json();
      return { success: true, enabled: Boolean(data.enabled) };
    } catch (error) {
      console.warn('[STT]', 'GET /api/stt/status network or parse error', error?.message || error);
      return { success: false, enabled: false };
    }
  }

  /**
   * POST /api/stt — transcribe a recorded audio blob.
   * @param {Blob} blob
   * @param {string|null} [language] en | el
   * @param {AbortSignal} [signal]
   * @returns {Promise<string>}
   */
  async speechToText(blob, language = null, signal = undefined) {
    const formData = new FormData();
    formData.append('file', blob, blob.name || 'recording.webm');
    if (language === 'en' || language === 'el') {
      formData.append('language', language);
    }

    let response;
    try {
      response = await fetch(buildApiUrl('/api/stt'), {
        method: 'POST',
        body: formData,
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal,
      });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      console.warn('[STT]', 'POST /api/stt fetch failed', error?.message || error);
      if (error && typeof error === 'object' && !error.code) {
        error.code = 'transcription-failed';
      }
      throw error;
    }

    const contentType = response.headers.get('Content-Type') || '';
    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      if (contentType.includes('application/json')) {
        try {
          const errJson = await response.json();
          if (errJson.error) {
            message = errJson.error;
          }
        } catch {
          /* ignore */
        }
      }
      console.warn('[STT]', 'POST /api/stt failed', { status: response.status, message });
      const err = new Error(message);
      if (response.status === 503 || response.status === 404) {
        err.code = 'stt-not-configured';
      } else {
        err.code = 'transcription-failed';
      }
      throw err;
    }

    const data = await response.json();
    return String(data.text || '').trim();
  }
}

export default new ApiService();
